import { findFirstNonEmptyString, notEmpty } from "@product/scmp-sdk";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { useEffect } from "react";
import { graphql, useFragment } from "react-relay";

import { useAppBarStickyHeightValue } from "~/components/app-bar/hooks";
import { ArticleComment } from "~/components/article/article-comment";
import { useSeriesArticleContext } from "~/components/article/article-render/series-article/contexts";
import { ContentSchemaRender } from "~/components/schema-render/content";
import { useImageUtils, useIntersection } from "~/lib/hooks";
import { formatInTimeZone } from "~/lib/utils";
import type { itemSeriesArticleSeriesItemResult$key } from "~/queries/__generated__/itemSeriesArticleSeriesItemResult.graphql";

import {
  Authors,
  AuthorsWrapper,
  Container,
  Content,
  Headline,
  ImagePane,
  ImageWrapper,
  LeftLine,
  NumberBlock,
  NumberCol,
  Pad,
  StyledAuthorEntityLink,
  StyledBaseImage,
  StyledIconPlayButton,
  StyledSeriesArticleItemType,
  Summary,
  Toolbar,
  Updated,
} from "./styles";

type Props = {
  className?: string;
  isSmallImage?: boolean;
  layout: "left" | "right";
  number: string;
  reference: itemSeriesArticleSeriesItemResult$key;
};

export const SeriesArticleItem: FunctionComponent<Props> = ({
  className,
  isSmallImage = false,
  layout,
  number,
  reference,
}) => {
  const { appBarStickyHeight } = useAppBarStickyHeightValue();
  const { getResizedImageUrl } = useImageUtils();
  const { handleUpdateScrollState } = useSeriesArticleContext();

  const seriesItem = useFragment(
    graphql`
      fragment itemSeriesArticleSeriesItemResult on SeriesItemResult {
        __typename
        ...typeSeriesArticleItemSeriesItemResult
        ...entityLink
        ... on Article {
          entityId
          headline
          socialHeadline
          subHeadline {
            json
          }
          updatedDate
          summary {
            json
          }
          authors {
            ...entityLink
            name
          }
          images {
            title
            style(filter: { style: "460x323" }) {
              url
            }
            type
          }
          ...articleCommentArticle
          ...contentSchemaRenderContent
        }
        ... on Video {
          entityId
          headline
          socialHeadline
          updatedDate
          summary {
            json
          }
          body {
            json
          }
          authors {
            ...entityLink
            name
          }
          images {
            title
            type
            style(filter: { style: "460x323" }) {
              url
            }
          }
          ...contentSchemaRenderContent
        }
        ... on Podcast {
          entityId
          headline
          socialHeadline
          updatedDate
          summary {
            json
          }
          body {
            json
          }
          authors {
            ...entityLink
            name
          }
          images {
            title
            style(filter: { style: "460x323" }) {
              url
            }
            type
          }
          ...contentSchemaRenderContent
        }
      }
    `,
    reference,
  );

  const authorsLength = seriesItem.authors?.length ?? 0;

  const renderImage = () => {
    const images = seriesItem.images?.filter(img => notEmpty(img)) ?? [];
    const mainImage = images?.find(image => image?.type === "mainImage") ?? first(images);

    const source = getResizedImageUrl(mainImage?.style?.url ?? "", 460);
    const alt = mainImage?.title ?? "";
    return (
      <StyledBaseImage
        $aspectRatio="4 / 3"
        $height="auto"
        $isSmall={isSmallImage}
        $objectFit="cover"
        alt={alt}
        src={source}
        disableLinkingData
      />
    );
  };

  const renderHeadline = () =>
    findFirstNonEmptyString(seriesItem.socialHeadline, seriesItem.headline);

  const renderAuthors = () =>
    seriesItem.authors?.map((author, index) => (
      <>
        {author && (
          <Authors>
            <StyledAuthorEntityLink reference={author}>{author.name}</StyledAuthorEntityLink>
            {index + 2 < authorsLength && <span>, </span>}
            {index + 2 === authorsLength && <span> and </span>}
          </Authors>
        )}
      </>
    )) ?? null;

  const renderSummary = () => {
    if (seriesItem?.summary?.json && seriesItem.summary.json.length > 0) {
      return <ContentSchemaRender reference={seriesItem} schema={seriesItem.summary?.json} />;
    }
    if (seriesItem?.subHeadline?.json && seriesItem.subHeadline.json.length > 0) {
      return <ContentSchemaRender reference={seriesItem} schema={seriesItem.subHeadline?.json} />;
    }
    if (seriesItem?.body?.json && seriesItem.body.json.length > 0) {
      return <ContentSchemaRender reference={seriesItem} schema={seriesItem.body?.json} />;
    }
  };

  const { captureIntersectionElement, intersection } = useIntersection({
    rootMargin: "-45% 0px -45%",
    threshold: [0.05],
  });

  useEffect(() => {
    if (!intersection?.isIntersecting) return;
    handleUpdateScrollState?.({
      highlightedEntityId: "",
      toScrollEntityId: seriesItem.entityId,
    });
  }, [handleUpdateScrollState, intersection, seriesItem]);

  return (
    <Container
      $position={layout}
      className={className}
      id={`series-item-${seriesItem.entityId}`}
      ref={captureIntersectionElement}
    >
      <Pad />
      <NumberCol>
        <NumberBlock>{number}</NumberBlock>
        <LeftLine />
      </NumberCol>
      <Content $position={layout}>
        <StyledSeriesArticleItemType reference={seriesItem} />
        <Headline reference={seriesItem}>{renderHeadline()}</Headline>
        <>{authorsLength > 0 && <AuthorsWrapper>by {renderAuthors()}</AuthorsWrapper>}</>
        <Summary reference={seriesItem}>{renderSummary()}</Summary>

        <Toolbar>
          {seriesItem?.updatedDate && (
            <Updated>{formatInTimeZone(seriesItem.updatedDate, "d MMM yyyy")}</Updated>
          )}
          {seriesItem.__typename === "Article" && (
            <ArticleComment reference={seriesItem} trackingPosition="rail" />
          )}
        </Toolbar>
      </Content>
      <ImagePane $isSmall={isSmallImage} $position={layout}>
        <ImageWrapper $stickyOffset={appBarStickyHeight} reference={seriesItem}>
          {seriesItem.__typename === "Video" && <StyledIconPlayButton />}
          {renderImage()}
        </ImageWrapper>
      </ImagePane>
    </Container>
  );
};

SeriesArticleItem.displayName = "SeriesArticleItem";
